import { useEffect, useMemo, useRef, useState } from "react";
import StudentLayout from "@/components/StudentLayout";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Loader2, GraduationCap, MessageCircleQuestion, Mic2, RotateCcw,
  Type, Tag, User, Zap, Sparkles, Gauge, MapPin, Link as LinkIcon, BookOpen,
  Copy, AlignLeft, Clock, History, FastForward, Repeat, HelpCircle, Quote,
  MessageCircle, Music, Users, Hash, Languages as LanguagesIcon, Lightbulb,
  PhoneCall, PhoneOff, AudioLines, Play, Check, Flame, Timer,
} from "lucide-react";
import { toast } from "sonner";
import { StatusPill, Bubble, UnifiedComposer } from "@/pages/tuition/TuitionChatParts";

const API = process.env.REACT_APP_BACKEND_URL;
// Language Learning is shared by students AND staff (teachers/admins). Pick the
// matching auth token + app shell for whoever is signed in.
const IS_STUDENT = () => localStorage.getItem("sa_user_type") === "student";
const TOKEN = () => localStorage.getItem(IS_STUDENT() ? "sa_student_token" : "sa_token");

// SIRI — the AI speaking teacher. Viseme frames driven by live audio for
// acoustic lip-sync while she speaks. Order: rest, small, AA, EE, OO.
const SIRI_FRAMES = [
  "https://static.prod-images.emergentagent.com/jobs/79263a26-113c-4193-9650-5fbb7dcbe4a1/images/6116bfa2d62fca53d7d755653e3688f1d6f03ed7d5ee10beb3edd196c3087b09.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/79263a26-113c-4193-9650-5fbb7dcbe4a1/images/12e76c7175d51e0bf072767ca51e2c0dc85d803456eafbe2dd1f9967aa676056.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/79263a26-113c-4193-9650-5fbb7dcbe4a1/images/0bf133b978655f129e873665e2a4d64f96e240ab47df9520928f18573120d273.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/79263a26-113c-4193-9650-5fbb7dcbe4a1/images/18bfccc221624a303264cb262f52f3dec6c1647a163c1b27bf262ec576e89e9a.jpeg",
  "https://static.prod-images.emergentagent.com/jobs/79263a26-113c-4193-9650-5fbb7dcbe4a1/images/a5ce49b554aa1702a2fcee3d14be2e62fdbcf07804c9475300691899f9669fe3.jpeg",
];

// Split SIRI's reply into the spoken (target-language) part and the Kannada tip.
const splitSiri = (text = "") => {
  let cut = text.length;
  for (const mk of ["---", "💡"]) { const i = text.indexOf(mk); if (i !== -1) cut = Math.min(cut, i); }
  const spoken = text.slice(0, cut).trim();
  let tip = text.slice(cut).replace(/^-+/, "").replace(/\*\*💡[^:]*:\*\*/, "").replace(/💡/, "").trim();
  return { spoken: spoken || text.trim(), tip };
};

const ICONS = {
  Type, Tag, User, Zap, Sparkles, Gauge, MapPin, Link: LinkIcon, BookOpen, Copy,
  AlignLeft, Clock, History, FastForward, Repeat, HelpCircle, Quote,
  MessageCircle, Music, Users, Hash,
};

const MODES = [
  { id: "learn", label: "Learn topics", icon: GraduationCap, desc: "Pick a topic — the AI teacher explains it in Kannada." },
  { id: "doubt", label: "Ask a doubt", icon: MessageCircleQuestion, desc: "Ask any grammar question, get a simple Kannada answer." },
  { id: "practice", label: "Practice speaking", icon: Mic2, desc: "Talk with the AI and get gentle corrections." },
];

export default function LanguageLearning() {
  const [langs, setLangs] = useState([]);
  const [language, setLanguage] = useState(null); // full object
  const [level, setLevel] = useState("beginner");
  const [mode, setMode] = useState("learn");
  const [activeTopic, setActiveTopic] = useState(null);

  const [messages, setMessages] = useState([]); // {id, role, content, ttsUrl}
  const [processing, setProcessing] = useState("");
  const [chatInput, setChatInput] = useState("");
  const [attachedImage, setAttachedImage] = useState(null);
  const [error, setError] = useState("");
  const [speed] = useState(1);
  const [recording, setRecording] = useState(false);
  const [recLevel, setRecLevel] = useState(0);
  const [siriSpeaking, setSiriSpeaking] = useState(false);
  const [showType, setShowType] = useState(false);
  const [continuous, setContinuous] = useState(false);
  const [voices, setVoices] = useState([]);
  const [voiceDefaults, setVoiceDefaults] = useState({});
  const [voice, setVoice] = useState(() => localStorage.getItem("sa_siri_voice") || "");
  const voiceRef = useRef(voice);

  const scrollerRef = useRef(null);
  const audioElRef = useRef(null);
  const siriAudioRef = useRef(null);
  const playedUrlRef = useRef(null);
  const continuousRef = useRef(false);
  const recordingRef = useRef(false);
  const processingRef = useRef("");
  const speechRef = useRef(false);
  const lastLoudRef = useRef(0);
  const recStartRef = useRef(0);
  const lastReqRef = useRef(null);
  const introduceRef = useRef(false);
  const mediaRecRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const Wrapper = IS_STUDENT() ? StudentLayout : Layout;

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/language/curriculum`, { headers: { Authorization: `Bearer ${TOKEN()}` }, credentials: "include" });
        const j = await r.json();
        setLangs(j.languages || []);
      } catch { toast.error("Couldn't load languages."); }
      try {
        const rv = await fetch(`${API}/api/language/voices`, { headers: { Authorization: `Bearer ${TOKEN()}` }, credentials: "include" });
        const jv = await rv.json();
        setVoices(jv.voices || []);
        setVoiceDefaults(jv.defaults || {});
      } catch { /* voice picker is optional */ }
    })();
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, processing]);

  // SIRI immersive: auto-play the newest spoken reply through our own audio el.
  useEffect(() => {
    if (mode !== "practice") return;
    const last = [...messages].reverse().find((m) => m.role === "assistant" && m.ttsUrl);
    if (last && last.ttsUrl !== playedUrlRef.current) {
      playedUrlRef.current = last.ttsUrl;
      const a = siriAudioRef.current;
      if (a) { try { a.src = last.ttsUrl; a.playbackRate = speed; a.play().catch(() => {}); } catch { /* noop */ } }
    }
  }, [messages, mode, speed]);

  const replaySiri = () => {
    const a = siriAudioRef.current;
    if (a && a.src) { try { a.currentTime = 0; a.play().catch(() => {}); } catch { /* noop */ } }
  };

  // Keep refs in sync for use inside animation-frame / audio callbacks.
  useEffect(() => { recordingRef.current = recording; }, [recording]);
  useEffect(() => { processingRef.current = processing; }, [processing]);
  useEffect(() => { voiceRef.current = voice; }, [voice]);

  const selectVoice = (v) => {
    setVoice(v);
    if (v) localStorage.setItem("sa_siri_voice", v);
    else localStorage.removeItem("sa_siri_voice");
  };

  const previewVoice = async (v) => {
    try {
      const r = await fetch(`${API}/api/language/voice_preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${TOKEN()}` },
        credentials: "include",
        body: JSON.stringify({ voice: v, language: language?.id || "english" }),
      });
      const j = await r.json();
      if (!r.ok || !j.tts_token) throw new Error(j.detail || "Preview failed.");
      const a = siriAudioRef.current;
      if (a) { a.src = `${API}/api/language/tts?token=${encodeURIComponent(j.tts_token)}`; a.play().catch(() => {}); }
    } catch (e) { toast.error(e.message || "Couldn't preview voice."); }
  };

  // Continuous "call" mode: auto-listen when idle (not recording, not busy).
  const beginListen = () => {
    if (!continuousRef.current) return;
    if (recordingRef.current || processingRef.current) return;
    startRecording();
  };

  const toggleContinuous = () => {
    const next = !continuous;
    setContinuous(next);
    continuousRef.current = next;
    if (next) {
      if (!recordingRef.current && !processingRef.current && !siriSpeaking) setTimeout(beginListen, 150);
    } else {
      stopRecording();
    }
  };

  // Called when SIRI finishes speaking — in call mode, start listening again.
  const handleSiriSpeakEnd = () => {
    setSiriSpeaking(false);
    setProcessing("");
    if (continuousRef.current) setTimeout(beginListen, 120);
  };

  const endpointFor = (m) => (m === "practice" ? "/api/language/practice" : "/api/language/teach");

  // Streaming reply — mirrors the AI Tutor's SSE + TTS-blob flow.
  async function runReply(history, topicId, useMode = mode) {
    lastReqRef.current = { history, topicId, useMode };
    setProcessing("thinking");
    setError("");
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 45000);
    const msgId = (crypto?.randomUUID && crypto.randomUUID()) || String(Date.now());
    const patch = (p) => setMessages((m) => m.map((x) => (x.id === msgId ? { ...x, ...p } : x)));
    setMessages((m) => [...m, { id: msgId, role: "assistant", content: "", ttsUrl: null }]);
    try {
      const body = useMode === "practice"
        ? { language: language.id, level, messages: history, introduce: introduceRef.current, voice: voiceRef.current }
        : { language: language.id, level, topic_id: topicId || null, messages: history };
      const resp = await fetch(`${API}${endpointFor(useMode)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${TOKEN()}` },
        credentials: "include",
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) {
        let detail = "Couldn't reach the teacher. Tap Retry.";
        try { const j = await resp.json(); detail = j.detail || detail; } catch { /* noop */ }
        throw new Error(detail);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "", full = "", meta = null, streamErr = null;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const ev of events) {
          const line = ev.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          const raw = line.slice(5).trim();
          if (!raw || raw === "[DONE]") continue;
          let obj; try { obj = JSON.parse(raw); } catch { continue; }
          if (obj.delta) { full += obj.delta; patch({ content: full }); }
          else if (obj.meta) {
            meta = obj.meta;
            const p = {};
            if (obj.meta.teacher_name) p.teacherName = obj.meta.teacher_name;
            // Start audio the INSTANT the token arrives (can be mid-stream,
            // before the Kannada tip finishes) — Siri speaks much sooner.
            if (obj.meta.tts_token) p.ttsUrl = `${API}/api/language/tts?token=${encodeURIComponent(obj.meta.tts_token)}`;
            patch(p);
          }
          else if (obj.error) streamErr = obj.error;
        }
      }
      if (streamErr) throw new Error(streamErr);
      setProcessing("");
      void meta;
    } catch (e) {
      setMessages((m) => m.filter((x) => !(x.id === msgId && !x.content)));
      const aborted = controller.signal.aborted || e.name === "AbortError";
      setError(aborted ? "The teacher took too long. Tap Retry." : (e.message || "Something went wrong."));
      setProcessing("");
    } finally { clearTimeout(timer); }
  }

  function startSession(nextMode, topic) {
    setMode(nextMode);
    setActiveTopic(topic || null);
    setMessages([]);
    setError("");
    // SIRI introduces herself by name once per login (tracked by the login token).
    if (nextMode === "practice") {
      const marker = (TOKEN() || "").slice(-16);
      const first = localStorage.getItem("sa_siri_intro") !== marker;
      introduceRef.current = first;
      if (first) localStorage.setItem("sa_siri_intro", marker);
    } else {
      introduceRef.current = false;
    }
    // Kickoff (empty history) — the backend generates the opening lesson/greeting.
    runReply([], topic?.id, nextMode);
  }

  function sendText(text) {
    const t = (text ?? chatInput).trim();
    if (!t || processing) return;
    setChatInput("");
    setAttachedImage(null);
    const history = [
      ...messages.filter((m) => m.content).map((m) => ({ role: m.role, content: m.content })),
      { role: "user", content: t },
    ];
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content: t }]);
    runReply(history, activeTopic?.id);
  }

  // Practice mode: ONE round-trip — upload audio, backend transcribes AND
  // streams Siri's reply in the same request (much faster than transcribe→chat).
  async function sendPracticeVoice(blob) {
    setProcessing("transcribing");
    setError("");
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 45000);
    const msgId = (crypto?.randomUUID && crypto.randomUUID()) || String(Date.now());
    const patch = (p) => setMessages((m) => m.map((x) => (x.id === msgId ? { ...x, ...p } : x)));
    let assistantAdded = false;
    try {
      const form = new FormData();
      form.append("audio", blob, "voice.webm");
      form.append("history", JSON.stringify(messages.filter((m) => m.content).map((m) => ({ role: m.role, content: m.content }))));
      const resp = await fetch(`${API}/api/language/practice_voice?language=${language.id}&level=${level}&introduce=${introduceRef.current}&voice=${encodeURIComponent(voiceRef.current)}`, {
        method: "POST", headers: { Authorization: `Bearer ${TOKEN()}` }, credentials: "include", body: form, signal: controller.signal,
      });
      if (!resp.ok || !resp.body) {
        let detail = "Couldn't reach Siri. Tap Retry.";
        try { const j = await resp.json(); detail = j.detail || detail; } catch { /* noop */ }
        throw new Error(detail);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "", full = "", streamErr = null, gotTranscript = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const ev of events) {
          const line = ev.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          const raw = line.slice(5).trim();
          if (!raw || raw === "[DONE]") continue;
          let obj; try { obj = JSON.parse(raw); } catch { continue; }
          if (obj.transcript !== undefined) {
            gotTranscript = true;
            const t = (obj.transcript || "").trim();
            if (!t) {
              setProcessing("");
              clearTimeout(timer);
              if (continuousRef.current) { setTimeout(beginListen, 120); return; }
              toast.error("Didn't catch that. Try again."); return;
            }
            const userMsg = { role: "user", content: t };
            const hist = [...messages.filter((m) => m.content).map((m) => ({ role: m.role, content: m.content })), userMsg];
            lastReqRef.current = { history: hist, topicId: null, useMode: "practice" };
            setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content: t }, { id: msgId, role: "assistant", content: "", ttsUrl: null }]);
            assistantAdded = true;
            setProcessing("thinking");
          } else if (obj.delta) { full += obj.delta; patch({ content: full }); }
          else if (obj.meta) {
            const p = {};
            if (obj.meta.teacher_name) p.teacherName = obj.meta.teacher_name;
            if (obj.meta.tts_token) p.ttsUrl = `${API}/api/language/tts?token=${encodeURIComponent(obj.meta.tts_token)}`;
            patch(p);
          }
          else if (obj.error) streamErr = obj.error;
        }
      }
      if (streamErr) throw new Error(streamErr);
      if (!gotTranscript) throw new Error("Couldn't process audio.");
      setProcessing("");
    } catch (e) {
      if (assistantAdded) setMessages((m) => m.filter((x) => !(x.id === msgId && !x.content)));
      const aborted = controller.signal.aborted || e.name === "AbortError";
      setProcessing("");
      if (continuousRef.current && !assistantAdded) { setTimeout(beginListen, 800); return; }
      setError(aborted ? "Siri took too long. Tap Retry." : (e.message || "Something went wrong."));
    } finally { clearTimeout(timer); }
  }

  async function sendAudio(blob) {
    if (mode === "practice") return sendPracticeVoice(blob);
    setProcessing("transcribing");
    try {
      const form = new FormData();
      form.append("audio", blob, "voice.webm");
      const r = await fetch(`${API}/api/language/transcribe?language=${language.id}`, {
        method: "POST", headers: { Authorization: `Bearer ${TOKEN()}` }, credentials: "include", body: form,
      });
      const j = await r.json();
      const text = (j.text || "").trim();
      if (!text) {
        setProcessing("");
        if (continuousRef.current) { setTimeout(beginListen, 200); return; }
        toast.error("Didn't catch that. Try again."); return;
      }
      sendText(text);
    } catch {
      setProcessing("");
      if (continuousRef.current) { setTimeout(beginListen, 800); return; }
      toast.error("Couldn't process audio.");
    }
  }

  async function startRecording() {
    if (recording || processing) return;
    try { audioElRef.current?.pause(); } catch { /* noop */ }
    try { siriAudioRef.current?.pause(); } catch { /* noop */ }
    setSiriSpeaking(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      const data = new Uint8Array(analyser.frequencyBinCount);
      speechRef.current = false;
      lastLoudRef.current = performance.now();
      recStartRef.current = performance.now();
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let peak = 0;
        for (let i = 0; i < data.length; i++) { const v = Math.abs(data[i] - 128) / 128; if (v > peak) peak = v; }
        setRecLevel(peak);
        // Call mode: detect speech, then auto-stop on ~0.8s of silence (14s max).
        if (continuousRef.current) {
          const now = performance.now();
          if (peak > 0.08) { speechRef.current = true; lastLoudRef.current = now; }
          const silent = now - lastLoudRef.current > 550;
          const tooLong = now - recStartRef.current > 14000;
          if ((speechRef.current && silent) || tooLong) {
            if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") { stopRecording(); return; }
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        try {
          stream.getTracks().forEach((t) => t.stop());
          cancelAnimationFrame(rafRef.current);
          try { await ctx.close(); } catch { /* noop */ }
          setRecLevel(0);
          if (!chunks.length) { setProcessing(""); return; }
          await sendAudio(new Blob(chunks, { type: rec.mimeType || "audio/webm" }));
        } catch { setProcessing(""); }
      };
      mediaRecRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (err) {
      toast.error(err?.name === "NotAllowedError" ? "Microphone access denied. Allow it to use voice." : (err?.message || "Could not start recording."));
    }
  }

  function stopRecording() {
    const rec = mediaRecRef.current;
    if (rec && rec.state !== "inactive") { try { rec.stop(); } catch { /* noop */ } }
    mediaRecRef.current = null;
    setRecording(false);
  }

  const resetToHome = () => { setLanguage(null); setMessages([]); setActiveTopic(null); setError(""); };

  // ── Language picker ───────────────────────────────────────────────
  if (!language) {
    return (
      <Wrapper>
        <div className="max-w-5xl mx-auto" data-testid="language-learning-home">
          <div className="flex items-center gap-2 mb-1">
            <LanguagesIcon className="h-6 w-6 text-indigo-500" />
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>Language Learning</h1>
          </div>
          <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">Learn a new language with your own AI teacher — explained fully in Kannada, so it's easy for everyone. 🌟</p>
          <SiriStreakCard />
          <div className="grid sm:grid-cols-2 gap-4">
            {langs.map((l) => (
              <button
                key={l.id}
                onClick={() => { setLanguage(l); setMode("learn"); }}
                className="group text-left rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 hover:border-indigo-400 hover:shadow-lg transition-all hover:-translate-y-0.5"
                data-testid={`language-card-${l.id}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-4xl">{l.flag}</span>
                  <div>
                    <div className="text-lg font-bold text-slate-900 dark:text-white" style={{ fontFamily: "Outfit" }}>{l.name} <span className="text-slate-400 font-normal">· {l.native}</span></div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{l.kn}</div>
                  </div>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300 mt-3">{l.blurb}</p>
                <div className="mt-3 text-xs font-semibold text-indigo-500 group-hover:translate-x-1 transition-transform inline-block">Start learning →</div>
              </button>
            ))}
            {langs.length === 0 && <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading languages…</div>}
          </div>
        </div>
      </Wrapper>
    );
  }

  const inChat = messages.length > 0 || processing;

  return (
    <Wrapper>
      <div className="max-w-4xl mx-auto flex flex-col" style={{ height: "calc(100vh - 8rem)" }} data-testid="language-learning-workspace">
        {/* Header */}
        <div className="flex items-center gap-3 pb-3 border-b border-slate-200 dark:border-slate-800">
          <Button variant="ghost" size="sm" className="rounded-full h-9 px-3" onClick={inChat ? () => { continuousRef.current = false; setContinuous(false); stopRecording(); setMessages([]); setActiveTopic(null); setError(""); } : resetToHome} data-testid="lang-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> {inChat ? "Topics" : "Languages"}
          </Button>
          <span className="text-2xl">{language.flag}</span>
          <div className="min-w-0">
            <div className="font-bold text-slate-900 dark:text-white truncate" style={{ fontFamily: "Outfit" }}>
              {language.name}{activeTopic ? ` · ${activeTopic.title}` : ""}
            </div>
            <div className="text-[11px] text-slate-400">Taught in Kannada · {level === "beginner" ? "Beginner" : "Intermediate"}</div>
          </div>
          {/* Level toggle */}
          <div className="ml-auto flex items-center rounded-full bg-slate-100 dark:bg-slate-800 p-0.5" data-testid="lang-level-toggle">
            {["beginner", "intermediate"].map((lv) => (
              <button
                key={lv}
                onClick={() => setLevel(lv)}
                className={`px-3 py-1 rounded-full text-xs font-semibold capitalize transition-colors ${level === lv ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-sm" : "text-slate-500"}`}
                data-testid={`lang-level-${lv}`}
              >
                {lv}
              </button>
            ))}
          </div>
        </div>

        {!inChat ? (
          <div className="flex-1 overflow-y-auto py-4">
            {/* Mode selector */}
            <div className="grid sm:grid-cols-3 gap-3 mb-6">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  onClick={() => m.id === "learn" ? setMode("learn") : startSession(m.id)}
                  className={`text-left rounded-2xl border p-4 transition-all hover:-translate-y-0.5 ${mode === m.id ? "border-indigo-400 bg-indigo-50/60 dark:bg-indigo-900/20" : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-indigo-300"}`}
                  data-testid={`lang-mode-${m.id}`}
                >
                  <m.icon className={`h-5 w-5 mb-2 ${mode === m.id ? "text-indigo-500" : "text-slate-400"}`} />
                  <div className="font-semibold text-sm text-slate-900 dark:text-white">{m.label}</div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{m.desc}</div>
                </button>
              ))}
            </div>

            {/* Topic grid (learn mode) */}
            {mode === "learn" && (
              <div data-testid="lang-topic-grid">
                <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Choose a topic</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {language.topics.map((t) => {
                    const Ico = ICONS[t.icon] || BookOpen;
                    return (
                      <button
                        key={t.id}
                        onClick={() => startSession("learn", t)}
                        className="text-left rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 hover:border-indigo-400 hover:shadow-md transition-all hover:-translate-y-0.5"
                        data-testid={`lang-topic-${t.id}`}
                      >
                        <Ico className="h-5 w-5 text-indigo-500 mb-1.5" />
                        <div className="text-sm font-semibold text-slate-900 dark:text-white leading-tight">{t.title}</div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{t.kn}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {mode === "doubt" && (
              <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 text-center" data-testid="lang-doubt-start">
                <Lightbulb className="h-8 w-8 text-amber-400 mx-auto mb-2" />
                <div className="font-semibold text-slate-900 dark:text-white">Ask any {language.name} doubt</div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 mb-4">Type or speak your question — the teacher answers simply, in Kannada.</p>
                <Button className="rounded-full bg-indigo-500 hover:bg-indigo-600" onClick={() => startSession("doubt")} data-testid="lang-doubt-begin">Start</Button>
              </div>
            )}
          </div>
        ) : mode === "practice" ? (
          <SiriStage
            messages={messages}
            processing={processing}
            recording={recording}
            recLevel={recLevel}
            siriSpeaking={siriSpeaking}
            error={error}
            showType={showType}
            setShowType={setShowType}
            chatInput={chatInput}
            setChatInput={setChatInput}
            onSendText={() => sendText()}
            onStartRec={startRecording}
            onStopRec={stopRecording}
            onReplay={replaySiri}
            onRetry={() => lastReqRef.current && runReply(lastReqRef.current.history, lastReqRef.current.topicId, lastReqRef.current.useMode)}
            langName={language.name}
            audioRef={siriAudioRef}
            onSpeakStart={() => setSiriSpeaking(true)}
            onSpeakEnd={handleSiriSpeakEnd}
            continuous={continuous}
            onToggleContinuous={toggleContinuous}
            voices={voices}
            voice={voice}
            defaultVoice={voiceDefaults[language.id] || ""}
            onSelectVoice={selectVoice}
            onPreviewVoice={previewVoice}
          />
        ) : (
          <>
            {/* Chat */}
            <div ref={scrollerRef} className="flex-1 min-h-0 overflow-y-auto py-4 space-y-4" data-testid="lang-messages">
              {messages.map((m, i) => (
                <Bubble
                  key={m.id || i}
                  role={m.role}
                  content={m.content}
                  ttsUrl={m.ttsUrl}
                  teacherName={m.teacherName || (language.id === "english" ? "Spoken English" : language.name)}
                  isLast={i === messages.length - 1}
                  audioRef={i === messages.length - 1 && m.role === "assistant" ? audioElRef : null}
                  speed={speed}
                  onAudioEnded={() => setProcessing("")}
                />
              ))}
              {processing && <StatusPill kind={processing} />}
              {error && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300 px-4 py-3 text-sm flex items-center justify-between gap-3" data-testid="lang-error">
                  <span>{error}</span>
                  {lastReqRef.current && !processing && (
                    <Button size="sm" variant="outline" onClick={() => runReply(lastReqRef.current.history, lastReqRef.current.topicId, lastReqRef.current.useMode)} className="shrink-0 rounded-full border-rose-300 text-rose-700" data-testid="lang-retry-btn">
                      <RotateCcw className="h-3.5 w-3.5 mr-1" /> Retry
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="pb-2">
              <UnifiedComposer
                value={chatInput}
                onChange={setChatInput}
                onSend={() => sendText()}
                busy={!!processing}
                image={attachedImage}
                onImage={setAttachedImage}
                onClearImage={() => setAttachedImage(null)}
                recording={recording}
                level={recLevel}
                onStartRec={startRecording}
                onStopRec={stopRecording}
              />
            </div>
          </>
        )}
      </div>
    </Wrapper>
  );
}

// ── Streak + minutes-practiced card (daily speaking habit) ────────────
function SiriStreakCard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/language/stats`, { headers: { Authorization: `Bearer ${TOKEN()}` }, credentials: "include" });
        if (r.ok) setStats(await r.json());
      } catch { /* card is optional */ }
    })();
  }, []);
  if (!stats) return null;
  const maxMin = Math.max(...stats.week.map((w) => w.minutes), 1);
  return (
    <div className="mb-6 rounded-2xl overflow-hidden border border-orange-200/70 dark:border-orange-900/40 bg-gradient-to-r from-orange-50 via-amber-50 to-white dark:from-slate-800 dark:via-slate-800 dark:to-slate-800 p-4 sm:p-5" data-testid="siri-streak-card">
      <div className="flex flex-wrap items-center gap-4 sm:gap-6">
        <div className="flex items-center gap-3">
          <div className={`h-12 w-12 rounded-2xl flex items-center justify-center ${stats.streak_days > 0 ? "bg-orange-500/15" : "bg-slate-200/60 dark:bg-slate-700"}`}>
            <Flame className={`h-7 w-7 ${stats.streak_days > 0 ? "text-orange-500" : "text-slate-400"}`} />
          </div>
          <div>
            <div className="text-2xl font-black text-slate-900 dark:text-white leading-none" style={{ fontFamily: "Outfit" }} data-testid="siri-streak-days">
              {stats.streak_days} <span className="text-sm font-bold text-slate-500">day{stats.streak_days === 1 ? "" : "s"}</span>
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">Practice streak {stats.streak_days > 0 && !stats.practiced_today ? "· practice today to keep it! 🔥" : stats.streak_days > 0 ? "🔥" : "· start today!"}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-teal-500/10 flex items-center justify-center">
            <Timer className="h-6 w-6 text-teal-500" />
          </div>
          <div>
            <div className="text-2xl font-black text-slate-900 dark:text-white leading-none" style={{ fontFamily: "Outfit" }} data-testid="siri-streak-today-minutes">
              {stats.today_minutes} <span className="text-sm font-bold text-slate-500">min today</span>
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 font-medium" data-testid="siri-streak-total-minutes">{stats.total_minutes} min practiced in total</div>
          </div>
        </div>
        {/* 7-day mini bars */}
        <div className="ml-auto flex items-end gap-1.5 h-12" data-testid="siri-streak-week">
          {stats.week.map((w) => (
            <div key={w.date} className="flex flex-col items-center gap-0.5">
              <div className="w-4 rounded-t-md bg-gradient-to-t from-orange-400 to-amber-300 dark:from-orange-500 dark:to-amber-400 transition-all"
                style={{ height: `${Math.max((w.minutes / maxMin) * 34, w.minutes > 0 ? 6 : 2)}px`, opacity: w.minutes > 0 ? 1 : 0.25 }}
                title={`${w.minutes} min`} />
              <span className="text-[8px] text-slate-400 font-semibold">{w.day[0]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── SIRI immersive speaking stage ─────────────────────────────────────
function SiriStage({
  messages, processing, recording, recLevel, siriSpeaking, error,
  showType, setShowType, chatInput, setChatInput, onSendText,
  onStartRec, onStopRec, onReplay, onRetry, langName,
  audioRef, onSpeakStart, onSpeakEnd, continuous, onToggleContinuous,
  voices, voice, defaultVoice, onSelectVoice, onPreviewVoice,
}) {
  const [showVoices, setShowVoices] = useState(false);
  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant" && m.content);
  const lastUser = [...messages].reverse().find((m) => m.role === "user" && m.content);
  const { spoken, tip } = splitSiri(lastAssistant?.content || "");
  const busy = processing === "thinking";

  // ── Lip-sync: pick a viseme frame from the live audio (amplitude + spectral shape) ──
  const [mouth, setMouth] = useState(0);
  const acRef = useRef(null);
  const srcRef = useRef(null);
  const anRef = useRef(null);
  const rafRef = useRef(null);
  const visRef = useRef({ idx: 0, at: 0 });

  const stopLoop = () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); rafRef.current = null; };

  const startLipSync = () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      if (!acRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        acRef.current = new AC();
        srcRef.current = acRef.current.createMediaElementSource(a);
        anRef.current = acRef.current.createAnalyser();
        anRef.current.fftSize = 512;
        anRef.current.smoothingTimeConstant = 0.5;
        srcRef.current.connect(anRef.current);
        anRef.current.connect(acRef.current.destination);
      }
      acRef.current.resume?.();
      const an = anRef.current;
      const bins = an.frequencyBinCount;
      const td = new Uint8Array(bins);
      const fd = new Uint8Array(bins);
      const tick = () => {
        an.getByteTimeDomainData(td);
        let peak = 0;
        for (let i = 0; i < bins; i++) { const v = Math.abs(td[i] - 128) / 128; if (v > peak) peak = v; }
        // energy-weighted spectral centroid (0..1) → vowel shape (round vs spread)
        an.getByteFrequencyData(fd);
        let sum = 0, wsum = 0;
        for (let i = 2; i < bins; i++) { const m = fd[i]; sum += m; wsum += m * i; }
        const centroid = sum > 0 ? (wsum / sum) / bins : 0;

        let target;
        if (peak < 0.045) target = 0;            // rest / closed
        else if (peak < 0.12) target = 1;         // small open
        else if (centroid > 0.24) target = 3;     // bright → EE (spread)
        else if (centroid < 0.11) target = 4;     // dark → OO (round)
        else target = 2;                          // mid → AA (wide open)

        // Hold each frame briefly to avoid flicker.
        const now = performance.now();
        const st = visRef.current;
        if (target !== st.idx && now - st.at > 55) { st.idx = target; st.at = now; setMouth(target); }
        else if (target === st.idx) { /* keep */ }
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch { /* analyser best-effort */ }
  };

  useEffect(() => () => { stopLoop(); try { acRef.current?.close(); } catch { /* noop */ } }, []);

  const handlePlay = () => { onSpeakStart(); startLipSync(); };
  const handleEnd = () => { onSpeakEnd(); stopLoop(); visRef.current = { idx: 0, at: 0 }; setMouth(0); };
  const frame = siriSpeaking ? mouth : 0;

  const status = recording ? "Listening…"
    : siriSpeaking ? "SIRI is speaking…"
    : processing === "transcribing" ? "Got it…"
    : busy ? "SIRI is thinking…"
    : continuous ? "Live conversation — just talk, SIRI is listening"
    : "Tap “Start conversation” to talk";

  const ringScale = recording ? 1 + Math.min(recLevel * 0.9, 0.6) : 1;

  return (
    <div className="flex-1 min-h-0 mt-3 rounded-3xl overflow-hidden relative flex flex-col items-center"
      style={{ background: "radial-gradient(120% 90% at 50% 0%, #1e2a4a 0%, #0b1220 55%, #060a14 100%)" }}
      data-testid="siri-stage">
      <style>{`
        @keyframes siriPulse { 0%,100%{ transform:scale(1); opacity:.55 } 50%{ transform:scale(1.14); opacity:.15 } }
        @keyframes siriFloat { 0%,100%{ transform:translateY(0) } 50%{ transform:translateY(-6px) } }
        @keyframes siriSpin { to { transform: rotate(360deg) } }
      `}</style>

      {/* top label */}
      <div className="w-full flex items-center gap-2 px-4 pt-3 pb-2 z-20">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
        </span>
        <span className="text-white font-bold tracking-tight" style={{ fontFamily: "Outfit" }} data-testid="siri-name">SIRI</span>
        <span className="text-white/40 text-xs">· {langName} speaking practice</span>
        {continuous && (
          <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-[11px] font-semibold px-2.5 py-1" data-testid="siri-live-pill">
            <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" /></span>
            LIVE
          </span>
        )}
      </div>

      {/* avatar frame */}
      <div className="relative mt-2 sm:mt-4" style={{ animation: "siriFloat 5s ease-in-out infinite" }}>
        {/* glow */}
        <div className="absolute inset-0 rounded-[28px]" style={{
          transform: `scale(${ringScale})`, transition: "transform .12s ease-out",
          boxShadow: recording ? "0 0 0 5px rgba(16,185,129,.35), 0 0 60px 10px rgba(16,185,129,.35)"
            : siriSpeaking ? "0 0 0 4px rgba(45,212,191,.45), 0 0 70px 14px rgba(45,212,191,.45)"
            : "0 0 0 3px rgba(129,140,248,.25), 0 0 40px 6px rgba(129,140,248,.25)",
        }} />
        {(siriSpeaking || recording) && (
          <div className="absolute -inset-2 rounded-[32px] pointer-events-none"
            style={{ background: recording ? "rgba(16,185,129,.16)" : "rgba(45,212,191,.16)", animation: "siriPulse 1.6s ease-in-out infinite" }} />
        )}
        <div className="relative w-60 h-72 sm:w-72 sm:h-[22rem] rounded-[28px] overflow-hidden ring-1 ring-white/15 shadow-2xl bg-slate-800 border-[6px] border-white/10">
          {SIRI_FRAMES.map((src, i) => (
            <img key={i} src={src} alt="SIRI" aria-hidden={i !== frame}
              className="absolute inset-0 h-full w-full object-cover object-top"
              style={{ opacity: i === frame ? 1 : 0, transition: "opacity .06s linear" }} />
          ))}
        </div>
      </div>

      {/* status */}
      <div className="mt-4 text-teal-200/90 text-sm font-medium h-5" data-testid="siri-status">{status}</div>

      {/* captions */}
      <div className="flex-1 w-full max-w-2xl px-5 overflow-y-auto mt-2">
        {lastUser && (
          <div className="text-center text-white/45 text-xs mb-2" data-testid="siri-you-said">You: “{lastUser.content}”</div>
        )}
        {spoken && (
          <div className="text-center text-white text-lg sm:text-2xl font-semibold leading-snug" style={{ fontFamily: "Outfit" }} data-testid="siri-caption">
            {spoken}
          </div>
        )}
        {tip && (
          <div className="mt-3 mx-auto max-w-lg rounded-2xl bg-amber-400/10 border border-amber-300/25 text-amber-100 px-4 py-2 text-sm text-center" data-testid="siri-tip">
            💡 {tip}
          </div>
        )}
        {!spoken && busy && (
          <div className="text-center text-white/40 text-sm" data-testid="siri-thinking">SIRI is getting ready…</div>
        )}
        {error && (
          <div className="mt-3 mx-auto max-w-md rounded-xl bg-rose-500/15 border border-rose-400/30 text-rose-100 px-4 py-2 text-sm flex items-center justify-between gap-3" data-testid="siri-error">
            <span>{error}</span>
            {!processing && <Button size="sm" variant="outline" onClick={onRetry} className="shrink-0 rounded-full border-rose-300/40 text-rose-100 bg-transparent hover:bg-rose-500/20"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Retry</Button>}
          </div>
        )}
      </div>

      {/* type row (optional) */}
      {showType && (
        <div className="w-full max-w-xl px-4 pb-1 z-20">
          <UnifiedComposer value={chatInput} onChange={setChatInput} onSend={onSendText} busy={!!processing} />
        </div>
      )}

      {/* voice picker */}
      {showVoices && (
        <div className="w-full max-w-2xl px-4 pb-2 z-20" data-testid="siri-voice-panel">
          <div className="rounded-2xl bg-white/10 border border-white/15 p-3" style={{ backdropFilter: "blur(14px)" }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80 text-xs font-semibold uppercase tracking-wider">Choose Siri's voice</span>
              <span className="text-white/40 text-[10px]">Tap ▶ to hear · tap a name to pick</span>
            </div>
            <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto pr-1">
              <button
                onClick={() => onSelectVoice("")}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${!voice ? "bg-teal-400/90 text-slate-900" : "bg-white/10 text-white hover:bg-white/20"}`}
                data-testid="siri-voice-default"
              >
                {!voice && <Check className="h-3 w-3" />} Default{defaultVoice ? ` (${defaultVoice})` : ""}
              </button>
              {voices.map((v) => (
                <span key={v.id}
                  className={`inline-flex items-center gap-1 rounded-full pl-3 pr-1 py-1 text-xs font-semibold transition-colors ${voice === v.id ? "bg-teal-400/90 text-slate-900" : "bg-white/10 text-white"}`}
                  data-testid={`siri-voice-${v.id}`}>
                  <button onClick={() => onSelectVoice(v.id)} className="inline-flex items-center gap-1 capitalize">
                    {voice === v.id && <Check className="h-3 w-3" />}
                    {v.id}
                    <span className={voice === v.id ? "text-slate-700" : "text-white/40"}>{v.gender === "male" ? "♂" : "♀"}</span>
                  </button>
                  <button onClick={() => onPreviewVoice(v.id)} title={`Hear ${v.id}`}
                    className={`h-5 w-5 rounded-full flex items-center justify-center ${voice === v.id ? "bg-slate-900/15 hover:bg-slate-900/25" : "bg-white/15 hover:bg-white/30"}`}
                    data-testid={`siri-voice-preview-${v.id}`}>
                    <Play className="h-2.5 w-2.5" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* controls */}
      <div className="w-full flex flex-col items-center gap-3 py-4 z-20">
        <button onClick={onToggleContinuous}
          className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-base font-semibold shadow-xl transition-colors ${continuous ? "bg-rose-500/90 hover:bg-rose-600 text-white" : "bg-emerald-500 hover:bg-emerald-600 text-white"}`}
          data-testid="siri-continuous-btn">
          {continuous ? <><PhoneOff className="h-5 w-5" /> End conversation</> : <><PhoneCall className="h-5 w-5" /> Start conversation</>}
        </button>

        <div className="flex items-center justify-center gap-6">
          <button onClick={onReplay} disabled={!!processing || recording} title="Replay"
            className="h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors disabled:opacity-40" data-testid="siri-replay-btn">
            <Repeat className="h-5 w-5" />
          </button>

          <button onClick={() => setShowType((v) => !v)} title="Type instead"
            className={`h-12 w-12 rounded-full flex items-center justify-center transition-colors ${showType ? "bg-teal-400/30 text-white" : "bg-white/10 hover:bg-white/20 text-white"}`} data-testid="siri-type-btn">
            <Type className="h-5 w-5" />
          </button>

          <button onClick={() => setShowVoices((v) => !v)} title="Change Siri's voice"
            className={`h-12 w-12 rounded-full flex items-center justify-center transition-colors ${showVoices ? "bg-teal-400/30 text-white" : "bg-white/10 hover:bg-white/20 text-white"}`} data-testid="siri-voice-btn">
            <AudioLines className="h-5 w-5" />
          </button>
        </div>
        {continuous && <div className="text-white/40 text-[11px]" data-testid="siri-continuous-hint">Hands-free · SIRI listens after she speaks and replies automatically</div>}
      </div>

      <audio ref={audioRef} className="hidden" onPlay={handlePlay} onEnded={handleEnd} onPause={handleEnd} />
    </div>
  );
}

